import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import API_BASE from '../apiBase';
import { getStoredUser } from '../utils/userStorage';
import { trackButtonClick, trackFormSubmit } from '../utils/gtm';

function CreateGroupDialog({ open, onClose, onGroupCreated }) {
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState('');

  const handleCreate = async () => {
    // Mocking creation
    if (groupName.trim()) {
      onGroupCreated({ id: Date.now().toString(), name: groupName });
      onClose();
    } else {
      setError('Group name is required.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} >
      <DialogTitle>Create New Group</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Group Name"
          type="text"
          fullWidth
          variant="outlined"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          error={!!error}
          helperText={error}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          trackButtonClick('create_group_dialog_cancel', 'project_creation_prompt');
          onClose();
        }}>Cancel</Button>
        <Button onClick={() => {
          trackButtonClick('create_group', 'project_creation_prompt', {
            group_name_provided: !!groupName
          });
          handleCreate();
        }} variant="contained">Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function ProjectCreationPrompt({ open, onClose, onSave, project, isTrial = false }) {
  const [localProject, setLocalProject] = useState({
    name: '',
    group_id: '',
    repository_type: 'github',
    analyzer_type: 'openai',
    analyzer_api_key: '',
    jira_enabled: false,
    azure_boards_enabled: false,
    trello_enabled: false,
    zoho_sprints_enabled: false,
    github_token: '',
    gitlab_token: '',
    bitbucket_token: '',
    jira_token: '',
    jira_url: '',
    jira_user: '',
    azure_boards_org: '',
    azure_boards_project: '',
    azure_boards_token: '',
    trello_api_key: '',
    trello_token: '',
    zoho_client_id: '',
    zoho_client_secret: '',
    zoho_grant_token: '',
    zoho_domain: 'zoho.in',
    zoho_team_id: '',
    zoho_project_id: '',
    user_emails: '',
    id: null,
    review_comment_mode: 'inline',
    azure_organization: '',
    azure_project: '',
    azure_token: '',
  });
  const [zohoTeams, setZohoTeams] = useState([]);
  const [zohoProjects, setZohoProjects] = useState([]);
  const [zohoAccessToken, setZohoAccessToken] = useState('');
  const [zohoRefreshToken, setZohoRefreshToken] = useState('');
  const [zohoDiscoveryLoading, setZohoDiscoveryLoading] = useState(false);
  const [zohoProjectsLoading, setZohoProjectsLoading] = useState(false);
  const [zohoDiscoveryError, setZohoDiscoveryError] = useState('');
  const [compliance, setCompliance] = useState(null);
  const [showCompliance, setShowCompliance] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [allowQdexLLM, setAllowQdexLLM] = useState(false);
  // Comment preferences
  const [commentPrefs, setCommentPrefs] = useState({ critical: true, medium: true, low: false });
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [trialMaxProjects, setTrialMaxProjects] = useState(null);
  const [businessContext, setBusinessContext] = useState('');
  const bcWordCount = businessContext.trim() ? businessContext.trim().split(/\s+/).length : 0;
  const bcTooLong = bcWordCount > 100;
  const [initialProjectState, setInitialProjectState] = useState(null); // Store initial state for edit mode
  const handleFetchZohoTeams = async () => {
    setZohoDiscoveryError('');
    setZohoDiscoveryLoading(true);
    setZohoTeams([]);
    setZohoProjects([]);
    setZohoAccessToken('');
    setZohoRefreshToken('');
    handleFieldChange({ target: { name: 'zoho_team_id', value: '' } });
    handleFieldChange({ target: { name: 'zoho_project_id', value: '' } });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/zoho-sprints/discover-teams`, {
        client_id: localProject.zoho_client_id,
        client_secret: localProject.zoho_client_secret,
        grant_token: localProject.zoho_grant_token,
        domain: localProject.zoho_domain,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setZohoTeams(res.data.teams || []);
      setZohoAccessToken(res.data.access_token || '');
      setZohoRefreshToken(res.data.refresh_token || '');
      if (!res.data.teams?.length) {
        setZohoDiscoveryError('No teams found in your Zoho Sprints account.');
      }
    } catch (err) {
      setZohoDiscoveryError(err.response?.data?.error || 'Failed to fetch teams. Check your credentials and try again.');
    } finally {
      setZohoDiscoveryLoading(false);
    }
  };

  const handleZohoTeamSelect = async (teamId) => {
    handleFieldChange({ target: { name: 'zoho_team_id', value: teamId } });
    handleFieldChange({ target: { name: 'zoho_project_id', value: '' } });
    setZohoProjects([]);
    if (!teamId || !zohoAccessToken) return;
    setZohoProjectsLoading(true);
    setZohoDiscoveryError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/zoho-sprints/discover-projects`, {
        access_token: zohoAccessToken,
        team_id: teamId,
        domain: localProject.zoho_domain,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setZohoProjects(res.data.projects || []);
      if (!res.data.projects?.length) {
        setZohoDiscoveryError('No projects found for this team.');
      }
    } catch (err) {
      setZohoDiscoveryError(err.response?.data?.error || 'Failed to fetch projects.');
    } finally {
      setZohoProjectsLoading(false);
    }
  };

  // Prefill for edit mode
  useEffect(() => {
    if (project && project.control_preference) {
      setCommentPrefs({
        critical: true,
        medium: !!project.control_preference.medium,
        low: !!project.control_preference.low,
      });
    } else {
      setCommentPrefs({ critical: true, medium: true, low: false });
    }
  }, [project]);

  const normalizeCompliance = (c) => {
    if (!c) return null;
    const n = { ...c };
    if (n.retention_basis === undefined && n.retention_days !== undefined) {
      n.retention_basis = 'payment basis';
    }
    if (n.consent_source_default === undefined && n.consent_source !== undefined) {
      n.consent_source_default = n.consent_source;
    }
    return n;
  };

  useEffect(() => {
    if (open) {
      // Track dialog/page view
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'page_view', {
          page_path: '/project-creation-prompt',
          page_title: 'Project Creation Prompt'
        });
      }
      (async () => {
        try {
          const { data: policy } = await axios.get(`${API_BASE}/api/compliance/policy`);
          if (project) {
            // Merge: policy fills gaps, project has priority
            setCompliance(normalizeCompliance({ ...(policy || {}), ...(project.compliance || {}) }) || null);
          } else {
            setCompliance(normalizeCompliance(policy) || null);
          }
        } catch (e) {
          // Fallback if policy fetch fails
          if (project) {
            setCompliance(normalizeCompliance(project.compliance) || null);
          }
        }
      })();
      // Determine subscription details and QDEX availability; also handle trial info in one call
      (async () => {
        try {
          const resp = await axios.get(`${API_BASE}/api/payments/current-subscription`);
          const plan = resp?.data?.plan;
          setAllowQdexLLM(!!(plan && (plan.ai_provided_by_us === true)));
          if (isTrial) {
            const sub = resp.data && resp.data.subscription;
            if (sub && sub.end_date) {
              const now = new Date();
              const end = new Date(sub.end_date);
              const diffMs = Math.max(0, end.getTime() - now.getTime());
              const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
              setTrialDaysLeft(days);
            }
            if (plan && typeof plan.max_projects === 'number') {
              setTrialMaxProjects(plan.max_projects);
            }
          } else {
            setTrialDaysLeft(null);
            setTrialMaxProjects(null);
          }
        } catch (e) {
          setAllowQdexLLM(false);
          if (isTrial) {
            // Fallbacks if API fails
            setTrialDaysLeft((prev) => prev ?? 30);
            setTrialMaxProjects((prev) => prev ?? 1);
          } else {
            setTrialDaysLeft(null);
            setTrialMaxProjects(null);
          }
        }
      })();
      if (project) {
        const projectData = {
          id: project.id || null,
          name: project.name || '',
          description: project.description || '',
          api_key: project.api_key || '',
          repository_type: project.repository_type || 'github',
          jira_enabled: project.jira_enabled || false,
          azure_boards_enabled: project.azure_boards_enabled || false,
          trello_enabled: project.trello_enabled || false,
          zoho_sprints_enabled: project.zoho_sprints_enabled || false,
          analyzer_type: project.analyzer_type || 'openai',
          review_comment_mode: project.review_comment_mode || 'inline',
          // Do NOT prefill secrets on edit; keep them empty to avoid accidental rotations
          github_token: '',
          gitlab_token: '',
          bitbucket_token: '',
          azure_organization: project.repository_config?.organization || '',
          azure_project: project.repository_config?.project || '',
          azure_token: '',
          // JIRA config
          jira_url: project.jira_config?.base_url || '',
          jira_user: project.jira_config?.username || '',
          jira_token: '',
          // Azure Boards config
          azure_boards_org: project.azure_boards_config?.organization || '',
          azure_boards_project: project.azure_boards_config?.project || '',
          azure_boards_token: '',
          // Trello config
          trello_api_key: '',
          trello_token: '',
          // Zoho Sprints config
          zoho_client_id: '',
          zoho_client_secret: '',
          zoho_grant_token: '',
          zoho_domain: project.zoho_sprints_config?.domain || 'zoho.in',
          zoho_team_id: project.zoho_sprints_config?.team_id || '',
          zoho_project_id: project.zoho_sprints_config?.project_id || '',
          // Analyzer config
          analyzer_api_key: '',
          // Additional emails (exclude current user)
          user_emails: (() => {
            const userInfo = getStoredUser() || {};
            const currentUserEmail = userInfo.email || '';
            const allEmails = Array.isArray(project.user_emails) ? project.user_emails : [project.user_emails || ''];
            return allEmails.filter(email => email && email !== currentUserEmail).join(', ');
          })(),
        };
        
        if (projectData.repository_type === "bitbucket") {
          projectData.review_comment_mode = "inline";
        }
        
        setLocalProject(projectData);
        setBusinessContext(project.business_context || '');
        // Store initial state for change detection in edit mode
        setInitialProjectState({
          ...projectData,
          business_context: project.business_context || '',
          jira_enabled: project.jira_enabled || false,
          azure_boards_enabled: project.azure_boards_enabled || false,
          trello_enabled: project.trello_enabled || false,
          zoho_sprints_enabled: project.zoho_sprints_enabled || false,
        });
      } else {
        // For new projects
        const defaultData = {
          name: '',
          description: '',
          api_key: '',
          repository_type: 'github',
          jira_enabled: false,
          azure_boards_enabled: false,
          trello_enabled: false,
          zoho_sprints_enabled: false,
          analyzer_type: 'openai',
          analyzer_api_key: '',
          review_comment_mode: 'inline',
          github_token: '',
          gitlab_token: '',
          bitbucket_token: '',
          azure_organization: '',
          azure_project: '',
          azure_token: '',
          jira_url: '',
          jira_user: '',
          trello_api_key: '',
          trello_token: '',
          jira_token: '',
          azure_boards_org: '',
          azure_boards_project: '',
          azure_boards_token: '',
          zoho_client_id: '',
          zoho_client_secret: '',
          zoho_grant_token: '',
          zoho_domain: 'zoho.in',
          zoho_team_id: '',
          zoho_project_id: '',
          user_emails: '',
        };
        setLocalProject(defaultData);
        setZohoTeams([]);
        setZohoProjects([]);
        setZohoAccessToken('');
        setZohoRefreshToken('');
        setZohoDiscoveryError('');
        setBusinessContext('');
        setInitialProjectState(null); // No initial state for new projects
      }
      setError('');
      setFieldErrors({});
    }
  }, [open, project]);

  const validateFields = () => {
    const errors = {};
    
    // Validate project name
    if (!localProject.name || !localProject.name.trim()) {
      errors.name = 'Project name is required.';
    }
    
    // Validate repository token based on type (only required for new projects, optional for editing)
    const isEditMode = !!project;
    if (!isEditMode) {
      // For new projects, tokens are required
      if (localProject.repository_type === 'github' && !localProject.github_token?.trim()) {
        errors.github_token = 'GitHub token is required.';
      } else if (localProject.repository_type === 'gitlab' && !localProject.gitlab_token?.trim()) {
        errors.gitlab_token = 'GitLab token is required.';
      } else if (localProject.repository_type === 'bitbucket' && !localProject.bitbucket_token?.trim()) {
        errors.bitbucket_token = 'Bitbucket token is required.';
      } else if (localProject.repository_type === 'azure') {
        if (!localProject.azure_organization?.trim()) {
          errors.azure_organization = 'Azure organization is required.';
        }
        if (!localProject.azure_project?.trim()) {
          errors.azure_project = 'Azure project is required.';
        }
        if (!localProject.azure_token?.trim()) {
          errors.azure_token = 'Azure token is required.';
        }
      }
    }
    // For edit mode, tokens are optional (leave empty to keep existing token)
    
    // Validate analyzer API key (unless qdex)
    // For edit mode, analyzer API key is optional (leave empty to keep existing value)
    if (!isEditMode && localProject.analyzer_type !== 'qdex' && !localProject.analyzer_api_key?.trim()) {
      errors.analyzer_api_key = 'Analyzer API key is required.';
    }
    
    // Validate JIRA fields if enabled
    // Only require fields if JIRA was newly enabled (not already enabled in edit mode)
    const wasJiraEnabled = initialProjectState?.jira_enabled || false;
    const isJiraNewlyEnabled = localProject.jira_enabled && !wasJiraEnabled;
    
    if (localProject.jira_enabled) {
      // If newly enabled, all fields are required
      // If already enabled, fields are optional (can be left empty to keep existing values)
      if (isJiraNewlyEnabled || !isEditMode) {
        if (!localProject.jira_url?.trim()) {
          errors.jira_url = 'Jira base URL is required.';
        }
        if (!localProject.jira_user?.trim()) {
          errors.jira_user = 'Jira username/email is required.';
        }
        if (!localProject.jira_token?.trim()) {
          errors.jira_token = 'Jira API token is required.';
        }
      }
    }

    // Validate Azure Boards fields if enabled
    const wasAzureBoardsEnabled = initialProjectState?.azure_boards_enabled || false;
    const isAzureBoardsNewlyEnabled = localProject.azure_boards_enabled && !wasAzureBoardsEnabled;
    
    if (localProject.azure_boards_enabled) {
      if (isAzureBoardsNewlyEnabled || !isEditMode) {
        if (!localProject.azure_boards_org?.trim()) {
          errors.azure_boards_org = 'Azure Boards organization is required.';
        }
        if (!localProject.azure_boards_project?.trim()) {
          errors.azure_boards_project = 'Azure Boards project is required.';
        }
        if (!localProject.azure_boards_token?.trim()) {
          errors.azure_boards_token = 'Azure Boards token is required.';
        }
      }
    }

    // Validate Trello fields if enabled
    const wasTrelloEnabled = initialProjectState?.trello_enabled || false;
    const isTrelloNewlyEnabled = localProject.trello_enabled && !wasTrelloEnabled;
    
    if (localProject.trello_enabled) {
      if (isTrelloNewlyEnabled || !isEditMode) {
        if (!localProject.trello_api_key?.trim()) {
          errors.trello_api_key = 'Trello API key is required.';
        }
        if (!localProject.trello_token?.trim()) {
          errors.trello_token = 'Trello token is required.';
        }
      }
    }

    // Validate Zoho Sprints fields if enabled
    const wasZohoEnabled = initialProjectState?.zoho_sprints_enabled || false;
    const isZohoNewlyEnabled = localProject.zoho_sprints_enabled && !wasZohoEnabled;

    if (localProject.zoho_sprints_enabled) {
      if (!localProject.zoho_team_id?.trim()) {
        errors.zoho_team_id = 'Team ID is required. Use "Fetch Teams & Projects" to select one.';
      }
      if (!localProject.zoho_project_id?.trim()) {
        errors.zoho_project_id = 'Project ID is required. Use "Fetch Teams & Projects" to select one.';
      }
      if (isZohoNewlyEnabled || !isEditMode) {
        if (!localProject.zoho_client_id?.trim()) {
          errors.zoho_client_id = 'Client ID is required.';
        }
        if (!localProject.zoho_client_secret?.trim()) {
          errors.zoho_client_secret = 'Client Secret is required.';
        }
        if (!zohoAccessToken && !localProject.zoho_grant_token?.trim()) {
          errors.zoho_grant_token = 'Click "Fetch Teams & Projects" to exchange your grant token.';
        }
      }
    }
    
    // Validate user emails format if provided
    if (localProject.user_emails?.trim()) {
      const emails = localProject.user_emails.split(',').map(email => email.trim()).filter(email => email);
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const invalidEmails = emails.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        errors.user_emails = `Invalid email format: ${invalidEmails.join(', ')}`;
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if at least one change has been made (for edit mode)
  const hasChanges = () => {
    if (!initialProjectState) return true; // New project, always has "changes"
    
    // Compare all relevant fields
    const currentState = {
      name: localProject.name || '',
      description: localProject.description || '',
      repository_type: localProject.repository_type || 'github',
      analyzer_type: localProject.analyzer_type || 'openai',
      jira_enabled: localProject.jira_enabled || false,
      azure_boards_enabled: localProject.azure_boards_enabled || false,
      trello_enabled: localProject.trello_enabled || false,
      zoho_sprints_enabled: localProject.zoho_sprints_enabled || false,
      review_comment_mode: localProject.review_comment_mode || 'inline',
      business_context: businessContext || '',
      user_emails: localProject.user_emails || '',
      jira_url: localProject.jira_url || '',
      jira_user: localProject.jira_user || '',
      azure_boards_org: localProject.azure_boards_org || '',
      azure_boards_project: localProject.azure_boards_project || '',
      trello_api_key: localProject.trello_api_key || '',
      zoho_team_id: localProject.zoho_team_id || '',
      zoho_project_id: localProject.zoho_project_id || '',
    };
    
    const initialState = {
      name: initialProjectState.name || '',
      description: initialProjectState.description || '',
      repository_type: initialProjectState.repository_type || 'github',
      analyzer_type: initialProjectState.analyzer_type || 'openai',
      jira_enabled: initialProjectState.jira_enabled || false,
      azure_boards_enabled: initialProjectState.azure_boards_enabled || false,
      zoho_sprints_enabled: initialProjectState.zoho_sprints_enabled || false,
      review_comment_mode: initialProjectState.review_comment_mode || 'inline',
      business_context: initialProjectState.business_context || '',
      user_emails: initialProjectState.user_emails || '',
      jira_url: initialProjectState.jira_url || '',
      jira_user: initialProjectState.jira_user || '',
      azure_boards_org: initialProjectState.azure_boards_org || '',
      azure_boards_project: initialProjectState.azure_boards_project || '',
      zoho_team_id: initialProjectState.zoho_team_id || '',
      zoho_project_id: initialProjectState.zoho_project_id || '',
    };
    
    // Check if any field changed
    const fieldsChanged = JSON.stringify(currentState) !== JSON.stringify(initialState);
    
    // Also check if any token/secret was provided (counts as a change)
    const hasTokenChanges = 
      (localProject.github_token && localProject.github_token.trim()) ||
      (localProject.gitlab_token && localProject.gitlab_token.trim()) ||
      (localProject.bitbucket_token && localProject.bitbucket_token.trim()) ||
      (localProject.azure_token && localProject.azure_token.trim()) ||
      (localProject.jira_token && localProject.jira_token.trim()) ||
      (localProject.azure_boards_token && localProject.azure_boards_token.trim()) ||
      (localProject.analyzer_api_key && localProject.analyzer_api_key.trim());
    
    return fieldsChanged || hasTokenChanges;
  };

  const handleSave = async () => {
    try {
      // Clear previous errors
      setError('');
      
      // For edit mode, check if at least one change has been made
      if (project && !hasChanges()) {
        setError('At least one change is required to save the project.');
        trackFormSubmit('project_creation', 'project_creation_prompt', false, {
          error: 'no_changes'
        });
        return;
      }
      
      // Validate all fields
      if (!validateFields()) {
        setError('Please fix the errors in the form before saving.');
        trackFormSubmit('project_creation', 'project_creation_prompt', false, {
          error: 'validation_failed'
        });
        return;
      }
      
      if (bcTooLong) {
        setError('Business context must be at most 100 words.');
        return;
      }
      const userInfo = getStoredUser() || {};
      const currentUserEmail = userInfo.email || '';
      if (!currentUserEmail) {
        setError('User not logged in. Please log in and try again.');
        return;
      }
      const createdByInfo = {
        name: userInfo.name || 'Unknown User',
        email: currentUserEmail
      };
      const additionalEmails = localProject.user_emails.split(',').map(email => email.trim()).filter(email => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return email && emailRegex.test(email);
      });
      const finalEmails = [...new Set([currentUserEmail, ...additionalEmails])];

      // Prepare repository config based on repository type; only include non-empty secrets
      let repositoryConfig = {};
      if (localProject.repository_type === 'github') {
        if (localProject.github_token.trim()) repositoryConfig = { token: localProject.github_token };
      } else if (localProject.repository_type === 'gitlab') {
        if (localProject.gitlab_token.trim()) repositoryConfig = { token: localProject.gitlab_token };
      } else if (localProject.repository_type === 'bitbucket') {
        repositoryConfig = {};
        if (localProject.bitbucket_token.trim()) repositoryConfig.token = localProject.bitbucket_token;
      } else if (localProject.repository_type === 'azure') {
        repositoryConfig = {
          organization: localProject.azure_organization || '',
          project: localProject.azure_project || '',
        };
        if (localProject.azure_token.trim()) repositoryConfig.token = localProject.azure_token;
      }

      const analyzerConfig = {};
      if (localProject.analyzer_api_key && localProject.analyzer_api_key.trim()) {
        analyzerConfig.api_key = localProject.analyzer_api_key.trim();
      }

      let jiraConfig = {};
      if (localProject.jira_enabled) {
        jiraConfig = {
          base_url: localProject.jira_url || '',
          username: localProject.jira_user || '',
        };
        if (localProject.jira_token && localProject.jira_token.trim()) {
          jiraConfig.api_token = localProject.jira_token.trim();
        }
      }

      let azureBoardsConfig = {};
      if (localProject.azure_boards_enabled) {
        azureBoardsConfig = {
          organization: localProject.azure_boards_org || '',
          project: localProject.azure_boards_project || '',
        };
        if (localProject.azure_boards_token && localProject.azure_boards_token.trim()) {
          azureBoardsConfig.token = localProject.azure_boards_token.trim();
        }
      }

      let trelloConfig = {};
      if (localProject.trello_enabled) {
        trelloConfig = {
          api_key: localProject.trello_api_key || '',
        };
        if (localProject.trello_token && localProject.trello_token.trim()) {
          trelloConfig.token = localProject.trello_token.trim();
        }
      }

      let zohoSprintsConfig = {};
      if (localProject.zoho_sprints_enabled) {
        zohoSprintsConfig = {
          team_id: localProject.zoho_team_id || '',
          project_id: localProject.zoho_project_id || '',
          domain: localProject.zoho_domain || 'zoho.in',
        };
        if (zohoAccessToken && zohoRefreshToken) {
          zohoSprintsConfig.access_token = zohoAccessToken;
          zohoSprintsConfig.refresh_token = zohoRefreshToken;
        } else if (localProject.zoho_grant_token?.trim()) {
          zohoSprintsConfig.grant_token = localProject.zoho_grant_token.trim();
        }
        if (localProject.zoho_client_id?.trim()) {
          zohoSprintsConfig.client_id = localProject.zoho_client_id.trim();
        }
        if (localProject.zoho_client_secret?.trim()) {
          zohoSprintsConfig.client_secret = localProject.zoho_client_secret.trim();
        }
      }

      const complianceToSave = compliance
        ? {
            ...compliance,
            // Backend project schema still reads consent_source; ensure it is present
            consent_source: compliance.consent_source || compliance.consent_source_default || compliance.consent_source,
          }
        : undefined;

      const projectDataToSave = {
        id: localProject.id,
        name: localProject.name,
        description: localProject.description || '',
        business_context: businessContext || '',
        group_id: localProject.group_id || '',
        user_emails: finalEmails,
        repository_type: localProject.repository_type,
        jira_enabled: localProject.jira_enabled || false,
        azure_boards_enabled: localProject.azure_boards_enabled || false,
        trello_enabled: localProject.trello_enabled || false,
        zoho_sprints_enabled: localProject.zoho_sprints_enabled || false,
        analyzer_type: localProject.analyzer_type,
        repository_config: repositoryConfig,
        jira_config: jiraConfig,
        azure_boards_config: azureBoardsConfig,
        trello_config: trelloConfig,
        zoho_sprints_config: zohoSprintsConfig,
        analyzer_config: analyzerConfig,
        review_comment_mode: localProject.review_comment_mode || 'inline',
        created_by_info: createdByInfo,
    compliance: complianceToSave,
    control_preference: {
      critical: true,
      medium: !!commentPrefs.medium,
      low: !!commentPrefs.low,
    }
      };

      await onSave(projectDataToSave);
      trackFormSubmit('project_creation', 'project_creation_prompt', true, {
        repository_type: localProject.repository_type,
        analyzer_type: localProject.analyzer_type,
        jira_enabled: localProject.jira_enabled,
        azure_boards_enabled: localProject.azure_boards_enabled,
        is_edit: !!localProject.id
      });
      onClose();
    } catch(err) {
      setError("Failed to save project. Please try again.");
      trackFormSubmit('project_creation', 'project_creation_prompt', false, {
        error: err?.message || 'save_failed'
      });
    }
  };

  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Make JIRA and Azure Boards mutually exclusive
    if (name === 'jira_enabled' && checked) {
      trackButtonClick(`jira_integration_enabled`, 'project_creation_prompt');
      setLocalProject(prev => ({
        ...prev,
        jira_enabled: true,
        azure_boards_enabled: false
      }));
      return;
    }
    
    if (name === 'azure_boards_enabled' && checked) {
      trackButtonClick(`azure_boards_integration_enabled`, 'project_creation_prompt');
      setLocalProject(prev => ({
        ...prev,
        azure_boards_enabled: true,
        jira_enabled: false
      }));
      return;
    }
    
    if (name === "repository_type" && value === "bitbucket") {
      trackButtonClick(`repository_type_${value}`, 'project_creation_prompt', {
        previous_type: localProject.repository_type
      });
      setLocalProject(prev => ({
        ...prev,
        [name]: value,
        review_comment_mode: "inline"
      }));
    } else {
      // Track important field changes
      if (name === 'repository_type') {
        trackButtonClick(`repository_type_${value}`, 'project_creation_prompt', {
          previous_type: localProject.repository_type
        });
      } else if (name === 'analyzer_type') {
        trackButtonClick(`analyzer_type_${value}`, 'project_creation_prompt', {
          previous_type: localProject.analyzer_type
        });
      } else if (name === 'review_comment_mode') {
        trackButtonClick(`review_comment_mode_${value}`, 'project_creation_prompt', {
          previous_mode: localProject.review_comment_mode
        });
      }
      setLocalProject(prev => ({
        ...prev,
        [name]: type === 'checkbox' || type === 'switch' ? checked : value
      }));
    }
  };

  // Compliance UI: toggle section; only data_region editable, others read-only
  const renderCompliance = () => {
    const region = compliance?.data_region || '';
    if (!showCompliance) {
      return (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" size="small" onClick={() => {
            trackButtonClick('check_compliance_data', 'project_creation_prompt');
            setShowCompliance(true);
          }}>
            Check compliance data
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f1f1f' }}>Compliance</Typography>
          <Button variant="text" size="small" onClick={() => {
            trackButtonClick('hide_compliance_data', 'project_creation_prompt');
            setShowCompliance(false);
          }}>Hide</Button>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="data_region"
              label="Data Region (editable)"
              value={region}
              onChange={(e) => setCompliance(prev => ({ ...(prev || {}), data_region: e.target.value }))}
              fullWidth
              placeholder="e.g., US, EU, India"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fffde7',
                  borderRadius: 1,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Retention Basis" value={compliance?.retention_basis ?? ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Lawful Basis" value={compliance?.lawful_basis ?? ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Consent Required" value={compliance?.consent_required ? 'Yes' : 'No'} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Consent Source Default" value={compliance?.consent_source_default ?? ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Purposes" value={(compliance?.purposes || []).join(', ')} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="PII Categories" value={(compliance?.pii_categories || []).join(', ')} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="DSR Contact" value={compliance?.dsr_contact ?? ''} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Anonymize On Export" value={compliance?.anonymize_on_export ? 'Yes' : 'No'} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 600,
          fontSize: '1.25rem',
          color: '#1f1f1f',
          pb: 2
        }}
      >
        {project?.id ? 'Edit Project' : 'Create Project'}
        <IconButton
          onClick={() => {
            trackButtonClick('close_project_dialog', 'project_creation_prompt', {
              is_edit: !!localProject.id
            });
            onClose();
          }}
          sx={{
            color: '#737373',
            '&:hover': {
              bgcolor: '#F1F5F9',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
        
        {isTrial && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              🎉 Welcome to Your Free Trial!
            </Typography>
            <Typography variant="body2">
              {(() => {
                const daysText = (trialDaysLeft != null) ? `${trialDaysLeft}-day` : 'trial';
                const projectsText = (trialMaxProjects != null) ? trialMaxProjects : 1;
                return (
                  <>
                    You're on a {daysText} free trial. Create your first project to explore our AI-powered code analysis features. <br/>
                    You can create up to {projectsText} project{projectsText === 1 ? '' : 's'} during your trial period.
                  </>
                );
              })()}
            </Typography>
          </Alert>
        )}
        
        <Box component="form" noValidate autoComplete="off" sx={{ pb: 2 }}>
          {/* Project Details Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1f1f1f', 
                mb: 3,
                fontSize: '1.125rem'
              }}
            >
              Project Details
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main', 
                  textDecoration: 'underline', 
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.dark' }
                }}
                onClick={() => {
                  trackButtonClick('setup_guide', 'project_creation_prompt');
                  window.open('/setup', '_blank');
                }}
              >
                Setup Guide
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField 
                  name="name" 
                  label="Project Name*" 
                  value={localProject.name} 
                  onChange={handleFieldChange} 
                  fullWidth 
                  required 
                  placeholder="Enter project name"
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  name="user_emails" 
                  label="Additional User Emails (comma-separated)" 
                  value={localProject.user_emails} 
                  onChange={handleFieldChange} 
                  fullWidth 
                  multiline
                  rows={2}
                  placeholder="email1@example.com, email2@example.com"
                  error={!!fieldErrors.user_emails}
                  helperText={fieldErrors.user_emails || (() => {
                    const userInfo = getStoredUser() || {};
                    const currentUserEmail = userInfo.email || 'Not logged in';
                    return `Current user email (${currentUserEmail}) is automatically included. Add additional emails separated by commas.`;
                  })()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="business_context"
                  label="Business Context"
                  value={businessContext}
                  onChange={(e) => setBusinessContext(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe the business context for this project"
                  error={bcTooLong}
                  helperText={`Max 100 words (${bcWordCount}/100)`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Repository Configuration Section */}
          <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1f1f1f', 
                mb: 3,
                  fontSize: '1.125rem'
                }}
              >
                Repository Configuration
              </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Repository Type</InputLabel>
                  <Select
                    name="repository_type"
                    value={localProject.repository_type}
                    onChange={handleFieldChange}
                    label="Repository Type"
                    sx={{
                      borderRadius: 1,
                    }}
                  >
                    <MenuItem value="github">GitHub</MenuItem>
                    <MenuItem value="gitlab">GitLab</MenuItem>
                    <MenuItem value="bitbucket">Bitbucket</MenuItem>
                    <MenuItem value="azure">Azure DevOps</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {localProject.repository_type === 'github' && (
                <Grid item xs={12} sm={6}>
                  <TextField 
                    name="github_token" 
                    label="GitHub Token*" 
                    type="password" 
                    value={localProject.github_token} 
                    onChange={handleFieldChange} 
                    fullWidth 
                    required={!project}
                    error={!!fieldErrors.github_token}
                    helperText={fieldErrors.github_token || (project ? "Leave empty to keep existing token" : "")}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                  />
                </Grid>
              )}
              {localProject.repository_type === 'gitlab' && (
                <Grid item xs={12} sm={6}>
                  <TextField 
                    name="gitlab_token" 
                    label="GitLab Token*" 
                    type="password" 
                    value={localProject.gitlab_token} 
                    onChange={handleFieldChange} 
                    fullWidth 
                    required={!project}
                    error={!!fieldErrors.gitlab_token}
                    helperText={fieldErrors.gitlab_token || (project ? "Leave empty to keep existing token" : "")}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                  />
                </Grid>
              )}
              {localProject.repository_type === 'bitbucket' && (
                <Grid item xs={12} sm={12}>
                  <TextField 
                    name="bitbucket_token" 
                    label="Bitbucket Access Token*" 
                    type="password" 
                    value={localProject.bitbucket_token} 
                    onChange={handleFieldChange} 
                    fullWidth 
                    required={!project}
                    error={!!fieldErrors.bitbucket_token}
                    helperText={fieldErrors.bitbucket_token || (project ? "Leave empty to keep existing token" : "")}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      }
                    }}
                  />
                </Grid>
              )}
              {localProject.repository_type === 'azure' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="azure_organization" 
                      label="Azure DevOps Organization*" 
                      value={localProject.azure_organization} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required 
                      placeholder="your-organization"
                      error={!!fieldErrors.azure_organization}
                      helperText={fieldErrors.azure_organization}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="azure_project" 
                      label="Azure DevOps Project*" 
                      value={localProject.azure_project} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required 
                      placeholder="your-project"
                      error={!!fieldErrors.azure_project}
                      helperText={fieldErrors.azure_project}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="azure_token" 
                      label="Azure DevOps PAT*" 
                      type="password" 
                      value={localProject.azure_token} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required={!project}
                      error={!!fieldErrors.azure_token}
                      helperText={fieldErrors.azure_token || (project ? "Leave empty to keep existing token" : "Personal Access Token")}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                        }
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* AI Analyzer Section */}
          <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1f1f1f', 
                mb: 3,
                  fontSize: '1.125rem'
                }}
              >
                AI Analyzer
              </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>AI Analyzer</InputLabel>
                  <Select
                    name="analyzer_type"
                    value={localProject.analyzer_type}
                    onChange={handleFieldChange}
                    label="AI Analyzer"
                    sx={{
                      borderRadius: 1,
                    }}
                  >
                    {allowQdexLLM && <MenuItem value="qdex">QDEX</MenuItem>}
                    <MenuItem value="gemini">Gemini</MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="claude">Claude</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  name="analyzer_api_key" 
                  label={localProject.analyzer_type === 'qdex' ? 'Analyzer API Key (not required for QDEX)' : 'Analyzer API Key*'} 
                  type="password" 
                  value={localProject.analyzer_api_key} 
                  onChange={handleFieldChange} 
                  fullWidth 
                  required={localProject.analyzer_type !== 'qdex' && !project}
                  disabled={localProject.analyzer_type === 'qdex'}
                  placeholder={localProject.analyzer_type === 'qdex' ? 'Not required' : 'Enter API key'}
                  error={!!fieldErrors.analyzer_api_key}
                  helperText={fieldErrors.analyzer_api_key || (project && localProject.analyzer_type !== 'qdex' ? "Leave empty to keep existing token" : "")}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          
          {/* Review Comment Mode Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1f1f1f', 
                mb: 3,
                fontSize: '1.125rem'
              }}
            >
              Review Comment Mode
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Review Comment Mode</InputLabel>
                  <Select
                    name="review_comment_mode"
                    value={localProject.review_comment_mode || "inline"}
                    onChange={handleFieldChange}
                    label="Review Comment Mode"
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="inline">Inline (line-by-line for changed lines)</MenuItem>
                    <MenuItem value="pr_comment">PR Comment (single summary)</MenuItem>
                  </Select>
                </FormControl>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#737373',
                    fontSize: '0.75rem',
                    mt: 1,
                    display: 'block'
                  }}
                >
                  {localProject.repository_type === "bitbucket" 
                    ? "Bitbucket projects use inline comments only due to API limits" 
                    : localProject.repository_type === "azure"
                    ? "Azure DevOps supports both PR and inline comment modes"
                    : "How should review comments be posted for this project?"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          
          {/* Jira Integration Section */}
          <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1f1f1f', 
                mb: 3,
                  fontSize: '1.125rem'
                }}
              >
                Jira Integration
              </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      name="jira_enabled" 
                      checked={localProject.jira_enabled} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleFieldChange({ target: { name: 'jira_enabled', value: true } });
                          handleFieldChange({ target: { name: 'azure_boards_enabled', value: false } });
                          handleFieldChange({ target: { name: 'trello_enabled', value: false } });
                          handleFieldChange({ target: { name: 'zoho_sprints_enabled', value: false } });
                        } else {
                          handleFieldChange(e);
                        }
                      }}
                      disabled={localProject.azure_boards_enabled || localProject.trello_enabled || localProject.zoho_sprints_enabled}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1f1f1f',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1f1f1f',
                        },
                      }}
                    />
                  } 
                  label="Enable Jira Integration" 
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: '#475569',
                    }
                  }}
                />
                {(localProject.azure_boards_enabled || localProject.trello_enabled || localProject.zoho_sprints_enabled) && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                    Disable other PM integrations to enable Jira
                  </Typography>
                )}
              </Grid>
              {localProject.jira_enabled && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="jira_url" 
                      label="Jira Base URL*" 
                      value={localProject.jira_url} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required 
                      placeholder="https://your-domain.atlassian.net"
                      error={!!fieldErrors.jira_url}
                      helperText={fieldErrors.jira_url}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="jira_user" 
                      label="Jira Username/Email*" 
                      value={localProject.jira_user} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required 
                      error={!!fieldErrors.jira_user}
                      helperText={fieldErrors.jira_user}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="jira_token" 
                      label="Jira API Token*" 
                      type="password" 
                      value={localProject.jira_token} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required={!project || !(initialProjectState?.jira_enabled)}
                      error={!!fieldErrors.jira_token}
                      helperText={fieldErrors.jira_token || (project && initialProjectState?.jira_enabled ? "Leave empty to keep existing token" : "")}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* Azure Boards Integration Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
              Azure Boards Integration
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      name="azure_boards_enabled" 
                      checked={localProject.azure_boards_enabled} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleFieldChange({ target: { name: 'azure_boards_enabled', value: true } });
                          handleFieldChange({ target: { name: 'jira_enabled', value: false } });
                          handleFieldChange({ target: { name: 'trello_enabled', value: false } });
                          handleFieldChange({ target: { name: 'zoho_sprints_enabled', value: false } });
                        } else {
                          handleFieldChange(e);
                        }
                      }}
                      disabled={localProject.jira_enabled || localProject.trello_enabled || localProject.zoho_sprints_enabled}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#0078d4',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0078d4',
                        },
                      }}
                    />
                  } 
                  label="Enable Azure Boards Integration" 
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: '#475569',
                    }
                  }}
                />
                {(localProject.jira_enabled || localProject.trello_enabled || localProject.zoho_sprints_enabled) && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                    Disable other PM integrations to enable Azure Boards
                  </Typography>
                )}
              </Grid>
              {localProject.azure_boards_enabled && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="azure_boards_org" 
                      label="Organization*" 
                      value={localProject.azure_boards_org} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required 
                      placeholder="your-organization"
                      helperText="Azure DevOps organization name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="azure_boards_project" 
                      label="Project*" 
                      value={localProject.azure_boards_project} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required 
                      placeholder="your-project"
                      helperText="Azure DevOps project name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      name="azure_boards_token" 
                      label="Personal Access Token (PAT)*" 
                      type="password" 
                      value={localProject.azure_boards_token} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required={!project || !(initialProjectState?.azure_boards_enabled)}
                      helperText={project && initialProjectState?.azure_boards_enabled ? "Leave empty to keep existing token" : "PAT with Work Items read access"}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* Trello Integration Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
              Trello Integration
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      name="trello_enabled" 
                      checked={localProject.trello_enabled} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleFieldChange({ target: { name: 'trello_enabled', value: true } });
                          handleFieldChange({ target: { name: 'jira_enabled', value: false } });
                          handleFieldChange({ target: { name: 'azure_boards_enabled', value: false } });
                          handleFieldChange({ target: { name: 'zoho_sprints_enabled', value: false } });
                        } else {
                          handleFieldChange(e);
                        }
                      }}
                      disabled={localProject.jira_enabled || localProject.azure_boards_enabled || localProject.zoho_sprints_enabled}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#0079bf',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0079bf',
                        },
                      }}
                    />
                  } 
                  label="Enable Trello Integration" 
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: '#475569',
                    }
                  }}
                />
                {(localProject.jira_enabled || localProject.azure_boards_enabled || localProject.zoho_sprints_enabled) && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                    Disable other PM integrations to enable Trello
                  </Typography>
                )}
              </Grid>
              {localProject.trello_enabled && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      name="trello_api_key" 
                      label="Trello API Key*" 
                      value={localProject.trello_api_key} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required={!project || !(initialProjectState?.trello_enabled)}
                      placeholder="your-api-key"
                      error={!!fieldErrors.trello_api_key}
                      helperText={fieldErrors.trello_api_key || (project && initialProjectState?.trello_enabled ? "Leave empty to keep existing API key" : "Get your API key from https://trello.com/app-key")}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      name="trello_token" 
                      label="Trello Token*" 
                      type="password" 
                      value={localProject.trello_token} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required={!project || !(initialProjectState?.trello_enabled)}
                      placeholder="your-token"
                      error={!!fieldErrors.trello_token}
                      helperText={fieldErrors.trello_token || (project && initialProjectState?.trello_enabled ? "Leave empty to keep existing token" : "Generate token from https://trello.com/app-key")}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="body2">
                        <strong>PR Title Format:</strong> Use Trello card ID or URL in your PR title to link to Trello cards.
                        <br />
                        Examples: <code>[TRELLO-abc12345]</code> or <code>https://trello.com/c/abc12345</code>
                      </Typography>
                    </Alert>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* Zoho Sprints Integration Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
              Zoho Sprints Integration
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      name="zoho_sprints_enabled" 
                      checked={localProject.zoho_sprints_enabled} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleFieldChange({ target: { name: 'zoho_sprints_enabled', value: true } });
                          handleFieldChange({ target: { name: 'jira_enabled', value: false } });
                          handleFieldChange({ target: { name: 'azure_boards_enabled', value: false } });
                          handleFieldChange({ target: { name: 'trello_enabled', value: false } });
                        } else {
                          handleFieldChange(e);
                        }
                      }}
                      disabled={localProject.jira_enabled || localProject.azure_boards_enabled || localProject.trello_enabled}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#f9a825',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#f9a825',
                        },
                      }}
                    />
                  } 
                  label="Enable Zoho Sprints Integration" 
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: '#475569',
                    }
                  }}
                />
                {(localProject.jira_enabled || localProject.azure_boards_enabled || localProject.trello_enabled) && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                    Disable other PM integrations to enable Zoho Sprints
                  </Typography>
                )}
              </Grid>
              {localProject.zoho_sprints_enabled && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      name="zoho_client_id" 
                      label="OAuth Client ID" 
                      value={localProject.zoho_client_id} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required={!project || !(initialProjectState?.zoho_sprints_enabled)}
                      placeholder="1000.XXXXXXXXXX.XXXXXXXXXX"
                      error={!!fieldErrors.zoho_client_id}
                      helperText={fieldErrors.zoho_client_id || (project && initialProjectState?.zoho_sprints_enabled ? "Leave empty to keep existing" : "From Zoho API Console")}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      name="zoho_client_secret" 
                      label="OAuth Client Secret" 
                      type="password"
                      value={localProject.zoho_client_secret} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      required={!project || !(initialProjectState?.zoho_sprints_enabled)}
                      placeholder="your-client-secret"
                      error={!!fieldErrors.zoho_client_secret}
                      helperText={fieldErrors.zoho_client_secret || (project && initialProjectState?.zoho_sprints_enabled ? "Leave empty to keep existing" : "From Zoho API Console")}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      name="zoho_grant_token" 
                      label="Grant Token" 
                      type="password"
                      value={localProject.zoho_grant_token} 
                      onChange={(e) => {
                        handleFieldChange(e);
                        setZohoTeams([]);
                        setZohoProjects([]);
                        setZohoAccessToken('');
                        setZohoRefreshToken('');
                        handleFieldChange({ target: { name: 'zoho_team_id', value: '' } });
                        handleFieldChange({ target: { name: 'zoho_project_id', value: '' } });
                      }}
                      fullWidth 
                      placeholder="1000.XXXXXXXXXX.XXXXXXXXXX"
                      error={!!fieldErrors.zoho_grant_token}
                      helperText={fieldErrors.zoho_grant_token || (project && initialProjectState?.zoho_sprints_enabled ? "Only needed to re-authenticate" : "Generate from Self Client in Zoho API Console")}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      name="zoho_domain" 
                      label="Zoho Domain" 
                      value={localProject.zoho_domain} 
                      onChange={handleFieldChange} 
                      fullWidth 
                      placeholder="zoho.in"
                      helperText="Your Zoho region: zoho.in, zoho.com, zoho.eu"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>

                  {(!project || !initialProjectState?.zoho_sprints_enabled || localProject.zoho_grant_token?.trim()) && (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={handleFetchZohoTeams}
                        disabled={zohoDiscoveryLoading || !localProject.zoho_client_id?.trim() || !localProject.zoho_client_secret?.trim() || !localProject.zoho_grant_token?.trim()}
                        sx={{ 
                          borderRadius: 2, 
                          textTransform: 'none',
                          bgcolor: '#f9a825',
                          '&:hover': { bgcolor: '#f57f17' },
                        }}
                      >
                        {zohoDiscoveryLoading ? <><CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} /> Fetching Teams...</> : 'Fetch Teams & Projects'}
                      </Button>
                      {zohoDiscoveryError && (
                        <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                          {zohoDiscoveryError}
                        </Alert>
                      )}
                    </Grid>
                  )}

                  {zohoTeams.length > 0 && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Team (Portal)</InputLabel>
                        <Select
                          value={localProject.zoho_team_id}
                          label="Team (Portal)"
                          onChange={(e) => handleZohoTeamSelect(e.target.value)}
                          sx={{ borderRadius: 2 }}
                        >
                          {zohoTeams.map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.name} ({t.id})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {zohoProjectsLoading && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 56 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">Loading projects...</Typography>
                      </Box>
                    </Grid>
                  )}

                  {zohoProjects.length > 0 && !zohoProjectsLoading && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Project</InputLabel>
                        <Select
                          value={localProject.zoho_project_id}
                          label="Project"
                          onChange={(e) => handleFieldChange({ target: { name: 'zoho_project_id', value: e.target.value } })}
                          sx={{ borderRadius: 2 }}
                        >
                          {zohoProjects.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name || p.id} ({p.id})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {project && initialProjectState?.zoho_sprints_enabled && !localProject.zoho_grant_token?.trim() && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                          Currently connected to Team <strong>{localProject.zoho_team_id}</strong>, Project <strong>{localProject.zoho_project_id}</strong>.
                          To change, enter a new Grant Token and click "Fetch Teams & Projects".
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="body2">
                        <strong>PR Title Format:</strong>{' '}
                        <code>[ZOHO-3]</code>, <code>P1#I-3</code>, or <code>QDE-I3</code>
                      </Typography>
                    </Alert>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* Comments Preferences */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1f1f1f', 
                mb: 2,
                fontSize: '1.125rem'
              }}
            >
              Comments
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              Limit code review comments to selected severities. Critical is always included.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControlLabel 
                  control={<Switch checked={true} disabled />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: '#1F2937' }}>Critical</Typography>
                  </Box>}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel 
                  control={<Switch checked={commentPrefs.medium} onChange={(e) => {
                    trackButtonClick(`comment_preference_medium_${e.target.checked ? 'enabled' : 'disabled'}`, 'project_creation_prompt');
                    setCommentPrefs(prev => ({ ...prev, medium: e.target.checked }));
                  }} />}
                  label={<Typography sx={{ fontWeight: 600, color: '#1F2937' }}>Medium</Typography>}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={<Switch checked={commentPrefs.low} onChange={(e) => {
                    trackButtonClick(`comment_preference_low_${e.target.checked ? 'enabled' : 'disabled'}`, 'project_creation_prompt');
                    setCommentPrefs(prev => ({ ...prev, low: e.target.checked }));
                  }} />}
                  label={<Typography sx={{ fontWeight: 600, color: '#1F2937' }}>Low</Typography>}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Compliance Section toggle placed at the bottom after Jira */}
          {renderCompliance()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '24px', gap: 2 }}>
        <Button 
          onClick={() => {
            trackButtonClick('cancel_project_creation', 'project_creation_prompt', {
              is_edit: !!localProject.id
            });
            onClose();
          }}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            color: '#737373',
            borderColor: '#E2E8F0',
            '&:hover': {
              borderColor: '#CBD5E1',
              bgcolor: '#F8FAFC',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            bgcolor: '#1f1f1f',
            color: '#fff',
            '&:hover': {
              bgcolor: '#0F172A',
            },
          }}
        >
          Save Project
        </Button>
      </DialogActions>
    </Dialog>
    <CreateGroupDialog open={showCreateGroup} onClose={() => {
      trackButtonClick('create_group_dialog_cancel', 'project_creation_prompt');
      setShowCreateGroup(false);
    }} onGroupCreated={(group) => {
      setLocalProject(prev => ({ ...prev, group_id: group.id }));
    }}/>
    </>
  );
}

export default ProjectCreationPrompt;